/* =========================================================
   TOTAL FOOTY CO — MOTION LAYER (JS half)
   Added 2026-07-29. Pairs with assets/motion.css.
   No framework, no build step, no dependencies.

   RESPONSIBILITIES
     1. Split headline text into masked words and play them in.
     2. Reveal block targets on intersect, staggered per group.
     3. Add a stagger delay to the pre-existing [data-reveal] system
        in assets/global.js without taking it over.

   CONTRACT WITH THE CSS
     - Everything is hidden by CSS at parse time under html.tfm.
       This file never hides anything, it only finishes things.
     - data-tfm="ready"  heading box may show, words still masked
     - data-tfm="in"     play
     - --tfm-d           per-element (or per-word) transition delay

   THREE NETS AGAINST A BLANK PAGE (all added/corrected 2026-07-29
   after the specificity bug in motion.css shipped a blank homepage)
     1. The inline bootstrap in layout/theme.liquid strips html.tfm
        after 1.2s unless window.__tfmReady is set. That flag is now
        set at the END of a successful init, not the start — setting it
        first meant a throw halfway through disarmed the very failsafe
        that was supposed to catch it.
     2. init() runs inside try/catch. Any throw strips html.tfm
        outright, which un-hides everything instantly.
     3. A watchdog runs 3s after load. If any target is inside the
        viewport and still has not been played, html.tfm-forced is
        added and motion.css force-shows every target with !important.
        Below-fold content is untouched, so it still animates on scroll.
   ========================================================= */
(function () {
  "use strict";

  var root = document.documentElement;

  /* html.tfm absent means one of: the visitor asked for reduced motion,
     the bootstrap did not run, or the failsafe already fired. In all
     three cases nothing is hidden, so there is nothing to reveal. */
  if (!root.classList.contains("tfm")) return;

  if (!("IntersectionObserver" in window)) {
    root.classList.remove("tfm");
    return;
  }

  /* ---- KEEP IN SYNC with the marked selector lists in motion.css ---- */
  var TEXT_SEL = ".hero__heading, .section-header h2, .section-header .h2, [data-tfm-text]";
  var BLOCK_SEL = ".hero .eyebrow, .hero__subheading, .hero__actions, .hero__media-wrap";
  var GRID_SEL = "#MainContent .section .grid > *:not([data-reveal])";

  /* Hero is a designed cascade on load, not a generic scroll reveal, so
     its members are pulled out of the observed set. */
  var HERO_SEL = ".hero .eyebrow, .hero__heading, .hero__subheading, .hero__actions, .hero__media-wrap";

  /* Everything the CSS hides. Used only by the watchdog. */
  var ALL_SEL = TEXT_SEL + ", " + BLOCK_SEL + ", " + GRID_SEL;

  var WORD_STEP = 58;      /* ms between words */
  var WORD_STEP_CAP = 480; /* a 20-word heading must not take 1.2s to finish */
  var GROUP_STEP = 90;     /* ms between siblings in a grid */
  var GROUP_CAP = 360;

  var isCart = document.body.classList.contains("template-cart");

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function delay(el, ms) {
    el.style.setProperty("--tfm-d", Math.round(ms) + "ms");
  }

  /* ---------------------------------------------------------
     WORD SPLITTING

     Recurses so nested inline elements survive. .hero__heading wraps
     its last word in <span class="hero__heading-accent"> to colour it;
     splitting the heading naively with innerHTML would destroy that.
     Here the accent span is left in place and the words INSIDE it are
     wrapped, so the colour still applies.

     Whitespace between words is re-inserted as a real text node rather
     than as margin, so line wrapping, text selection and copy-paste
     all behave exactly as they did before the split.
     --------------------------------------------------------- */
  function splitWords(el, baseDelay) {
    /* Guards. Any of these means the heading is not a plain run of
       words and is safer left whole — the caller falls back to
       revealing it as a single block. */
    if (el.querySelector("img, svg, input, button, .tfm-w")) return false;

    var words = (el.textContent || "").trim().split(/\s+/).filter(Boolean);
    if (!words.length || words.length > 26) return false;
    for (var i = 0; i < words.length; i++) {
      /* A word longer than the mask can hold may need to break
         mid-word at 320px, and a mid-word break inside an
         overflow:hidden box gets its second half clipped. */
      if (words[i].length > 18) return false;
    }

    var index = 0;

    function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var text = child.nodeValue;
          if (!text || !text.trim()) return;

          var frag = document.createDocumentFragment();
          text.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(" "));
              return;
            }
            var outer = document.createElement("span");
            outer.className = "tfm-w";
            var inner = document.createElement("span");
            inner.className = "tfm-w__i";
            inner.textContent = part;
            delay(inner, baseDelay + Math.min(index * WORD_STEP, WORD_STEP_CAP));
            index++;
            outer.appendChild(inner);
            frag.appendChild(outer);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== "BR") {
          walk(child);
        }
      });
    }

    walk(el);
    return index > 0;
  }

  /* ---------------------------------------------------------
     PLAY

     "ready" and "in" are set in separate frames on purpose. Setting
     both in the same frame lets the browser coalesce them into one
     style recalculation, the transition never has a start value to
     interpolate from, and the words appear instantly.
     --------------------------------------------------------- */
  function play(el) {
    if (el.getAttribute("data-tfm") === "in") return;
    el.setAttribute("data-tfm", "ready");
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.setAttribute("data-tfm", "in");
      });
    });
  }

  /* Already scrolled past — e.g. a reload that restores scroll position,
     or a link to an anchor halfway down. Nothing to animate, and if we
     waited for an intersection that will never come the content would
     stay invisible for good. */
  function isAbove(el) {
    return el.getBoundingClientRect().bottom < 0;
  }

  /* ---------------------------------------------------------
     OBSERVER
     -8% bottom margin so a reveal starts just after the element has
     properly entered the frame rather than the instant its top edge
     crosses the fold.
     --------------------------------------------------------- */
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      play(entry.target);
    });
  }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });

  function observe(el) {
    if (isAbove(el)) { el.setAttribute("data-tfm", "in"); return; }
    io.observe(el);
  }

  /* ---------------------------------------------------------
     INIT
     Runs once on load and again per section in the theme editor.
     --------------------------------------------------------- */
  function initText(scope) {
    qsa(TEXT_SEL, scope).forEach(function (el) {
      if (el.hasAttribute("data-tfm") || el.closest(".drawer")) return;

      /* A heading sitting inside a [data-reveal] block should land just
         after the block it is part of, not race it. */
      var base = el.closest("[data-reveal]") ? 120 : 0;

      var didSplit = splitWords(el, base);
      if (!didSplit) delay(el, base);

      /* The hero cascade drives .hero__heading — but ONLY if the heading
         is actually inside a .hero, because that is what scheduleHero
         looks for. A .hero__heading used outside a .hero would
         otherwise be hidden by the CSS and never played by anyone. */
      if (el.matches(".hero__heading") && el.closest(".hero")) return;
      observe(el);
    });
  }

  function initBlocks(scope) {
    var blocks = qsa(BLOCK_SEL, scope);
    if (!isCart) blocks = blocks.concat(qsa(GRID_SEL, scope));

    blocks.forEach(function (el) {
      if (el.hasAttribute("data-tfm") || el.closest(".drawer")) return;

      /* Stagger against matching siblings so a four-up grid cascades
         left to right instead of all four arriving together. */
      var step = 0;
      var sib = el.previousElementSibling;
      while (sib) {
        if (sib.matches(GRID_SEL) || sib.matches(BLOCK_SEL)) step++;
        sib = sib.previousElementSibling;
      }
      delay(el, Math.min(step * GROUP_STEP, GROUP_CAP));

      if (el.matches(HERO_SEL) && el.closest(".hero")) return; /* hero cascade drives these */
      observe(el);
    });
  }

  /* Stagger the pre-existing [data-reveal] system rather than replacing
     it. assets/global.js still owns the .is-visible flip; all we do is
     give siblings a delay so a row of four does not arrive as a slab.
     base.css already declares the transition, so a transition-delay is
     the whole change. */
  function staggerLegacyReveals(scope) {
    qsa("[data-reveal]", scope).forEach(function (el) {
      if (el.style.transitionDelay) return;
      var step = 0;
      var sib = el.previousElementSibling;
      while (sib) {
        if (sib.hasAttribute("data-reveal")) step++;
        sib = sib.previousElementSibling;
      }
      if (step) el.style.transitionDelay = Math.min(step * GROUP_STEP, GROUP_CAP) + "ms";
    });
  }

  /* ---------------------------------------------------------
     HERO CASCADE
     Hand-timed rather than generic: the hero is the one composition on
     the site where the order things arrive in is a design decision.
     Media first so the page has an anchor, then the label, then the
     headline unmasking word by word, then the supporting copy, then
     the buttons.
     --------------------------------------------------------- */
  var heroPlayed = false;

  function playHero() {
    if (heroPlayed) return;
    heroPlayed = true;

    var steps = [
      [".hero__media-wrap", 40],
      [".hero .eyebrow", 120],
      [".hero__heading", 200],
      [".hero__subheading", 430],
      [".hero__actions", 530]
    ];

    steps.forEach(function (pair) {
      var el = document.querySelector(pair[0]);
      if (!el || el.hasAttribute("data-tfm-played")) return;
      el.setAttribute("data-tfm-played", "");

      if (pair[0] === ".hero__heading") {
        /* Re-base the word delays onto the hero's own schedule. */
        var words = qsa(".tfm-w__i", el);
        if (words.length) {
          words.forEach(function (w, i) {
            delay(w, pair[1] + Math.min(i * WORD_STEP, WORD_STEP_CAP));
          });
        } else {
          delay(el, pair[1]);
        }
      } else {
        delay(el, pair[1]);
      }

      play(el);
    });
  }

  /* The intro overlay owns the first ~1.15s of the homepage. Starting
     the hero cascade underneath it would mean the whole thing plays out
     behind an opaque wipe, and the visitor sees a hero that has already
     finished animating the moment the wipe lifts.

     snippets/intro-reveal.liquid does not emit an event when it is
     done — it just adds .tfc-hide to itself, on the normal path and on
     skip/Escape alike. Watching for that class is therefore more
     reliable than adding an event to the snippet, and it means the
     intro file does not have to know this file exists. The timeout is
     the failsafe: if the class never lands, the hero still plays. */
  function scheduleHero() {
    if (!document.querySelector(".hero")) return;

    var intro = document.getElementById("tfc-intro");
    if (!intro || !intro.classList.contains("tfc-active")) {
      playHero();
      return;
    }

    var mo = new MutationObserver(function () {
      if (intro.classList.contains("tfc-hide")) {
        mo.disconnect();
        playHero();
      }
    });
    mo.observe(intro, { attributes: true, attributeFilter: ["class"] });

    window.setTimeout(function () {
      mo.disconnect();
      playHero();
    }, 1700);
  }

  /* ---------------------------------------------------------
     WATCHDOG — net #3.

     Everything above can be correct and the page can still end up with
     invisible content, because the hiding is done by a stylesheet and
     the revealing is done here. Any mismatch between the two — a
     renamed class, a selector that outranks the reveal rule, a section
     that never intersects because an ancestor is transformed — shows up
     as content that is simply not there.

     So: 3s after load, look at every target that is currently inside
     the viewport. If any of them has not been played, give up on
     animating and force the whole layer visible. Content below the fold
     is deliberately not checked, so it still animates on scroll.
     --------------------------------------------------------- */
  function inViewport(el) {
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    return r.top < (window.innerHeight || 0) && r.bottom > 0;
  }

  function watchdog() {
    if (!root.classList.contains("tfm")) return;
    var stuck = qsa(ALL_SEL).filter(function (el) {
      if (el.closest(".drawer")) return false;
      if (el.getAttribute("data-tfm") === "in") return false;
      return inViewport(el);
    });
    if (stuck.length) {
      root.classList.add("tfm-forced");
      if (window.console && console.warn) {
        console.warn("[tfm] " + stuck.length + " target(s) never revealed; forcing visible.", stuck);
      }
    }
  }

  function armWatchdog() {
    window.setTimeout(watchdog, 3000);
  }

  function init(scope) {
    initText(scope);
    initBlocks(scope);
    staggerLegacyReveals(scope);
  }

  /* Net #2. A throw anywhere in setup un-hides everything rather than
     leaving a half-initialised page with invisible sections. */
  try {
    init(document);
    scheduleHero();
  } catch (err) {
    root.classList.remove("tfm");
    if (window.console && console.error) {
      console.error("[tfm] motion layer failed, reverting to static page.", err);
    }
    return;
  }

  /* Net #1. Only now is the bootstrap told to stand down. If anything
     above had thrown we would have returned already, and if this line
     is never reached the inline failsafe in theme.liquid strips
     html.tfm at 1.2s. */
  window.__tfmReady = true;

  if (document.readyState === "complete") {
    armWatchdog();
  } else {
    window.addEventListener("load", armWatchdog, { once: true });
  }

  /* Theme editor: a re-rendered section arrives with none of this
     applied, and its content is hidden by the parse-time CSS. */
  document.addEventListener("shopify:section:load", function (event) {
    var scope = event.target || document;
    try {
      init(scope);
      if (scope.querySelector && scope.querySelector(".hero")) {
        heroPlayed = false;
        qsa("[data-tfm-played]", scope).forEach(function (el) {
          el.removeAttribute("data-tfm-played");
        });
        playHero();
      }
    } catch (err) {
      root.classList.add("tfm-forced");
    }
    window.setTimeout(watchdog, 1200);
  });
})();
